import * as React from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { PagesHelper } from "./common/PagesHelper";
import { getHeadersConfig } from "./utils/config";
import ErrorHandler from "./utils/ErrorHandler";
import { PageName, PageState } from "./utils/types";
import { ResponseStatus, UserListResponseDto, UserPermissions } from "./utils/types";
import { Switch, Route, Redirect, useLocation, useHistory } from "react-router-dom";
import urlParser from "./utils/urlParser";
import CurrentUserBar from "./app/CurrentUserBar";
import { Alert } from "react-bootstrap";
import StateHelper from "./utils/StateHelper";
import ProductsPage from "./pages/ProductsPage";
import PerformanceStatsPage from "./pages/PerformanceStatsPage";
import TrackingLinksLookupPage from "./pages/TrackingLinksLookup";
import LookupPage from "./pages/LookupPage";
import LiveLogsPage from "./pages/LiveLogsPage";
import Loading from "./utils/Loding";
import { setDocumentTitle } from "./utils/documentTitle";
import { joinURLs } from "./utils/urlUtil";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import InventoryPage from "./pages/InventoryPage";
import AuthUserPage from "./pages/AuthUserPage";

function ErrorPage(){

  return (
    <Row>
      <Col xl={3}></Col>
      <Col xl={6}>
        <Alert
            title={"Unable to connect to dashboard server."}
            key="alertServerConnectionError"
            variant="danger"
            className="text-center border border-danger"
          >
            Unable to connect to the dashboard server. Please check your internet connection and try reloading this page.
            Contact support@pronomix.ai if the problem continues.
        </Alert>
      </Col>
      <Col xl={3}></Col>
    </Row>
  );
}

function PageContainer(props:any){
  if (!props.currentUser) {
    return null
  }

  return (
    <>
      <Row>
        <Col>
          <Switch>
            <Route
              exact
              path="/"
              render={() => {
                  return ( props.currentUser !== null ? <Redirect to={props.productsUrl} /> : <Redirect to="/" />)
              }}
            />

            <Route path={"/" + PageName.products}>
              <ProductsPage currentUser={props.currentUser} />
            </Route>

            <Route path={"/" + PageName.performanceStats}>
              <PerformanceStatsPage currentUser={props.currentUser} />
            </Route>

            <Route path={"/" + PageName.trackingLinksLookup}>
              <TrackingLinksLookupPage currentUser={props.currentUser} />
            </Route>

            <Route path={"/" + PageName.lookup}>
              <LookupPage currentUser={props.currentUser} /> 
            </Route>

            <Route path={"/" + PageName.liveLogs}>
              <LiveLogsPage currentUser={props.currentUser} /> 
            </Route>

            <Route path={"/" + PageName.inventory}>
              <InventoryPage currentUser={props.currentUser} /> 
            </Route>

            <Route path={"/" + PageName.authUser}>
              <AuthUserPage currentUser={props.currentUser} />
            </Route>

          </Switch>
        </Col>
      </Row>
    </>
  );
}

function usersMe(setCurrentUser: any, findUsersList: any, setError: any) {
  const NETWOR_ERROR = "NETWOR_ERROR";
  const config = getHeadersConfig(true);

  const permissions = Object.values(UserPermissions).join(",");

  const url = joinURLs("2/users/me", process.env.REACT_APP_REPORTING_API_BASE_URL);

  axios
    .get(url, {
      params: {
        checkPermissions: permissions
      },
      ...config
    })
    .then(function (response: any) {
      let canViewAs : boolean = response.data && 
        response.data.permissions && 
        response.data.permissions.includes(UserPermissions.apptapUsersList) && 
        response.data.permissions.includes(UserPermissions.apptapDashboardsViewAsAnyUser);

      if (response.data && response.data.actualUser) {
        canViewAs = response.data.actualUser.permissions && 
        response.data.actualUser.permissions.includes(UserPermissions.apptapUsersList) && 
        response.data.actualUser.permissions.includes(UserPermissions.apptapDashboardsViewAsAnyUser);
      }

      if (canViewAs) {
        findUsersList(false);
      }

      window.current_user = {
        loginEmail: response.data.email,
        ...response.data.clinks || {}
      };
      window.permissions = response.data.permissions ? response.data.permissions : [];
      const pageState: PageState = StateHelper.getPageState();
      setDocumentTitle(pageState.page, response.data);
      StateHelper.setTrafficSourcesDtoState(null);
      setCurrentUser(response.data);
      StateHelper.setUserState(response.data);
    })
    .catch(function (error) {
      if( ErrorHandler.isNetworkError(error) ){
        setError(NETWOR_ERROR);
        return;
      }

      ErrorHandler.handleAuthError(error, setError);
    }
  );
}

function App() {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const [error, setError] = React.useState(null);
  const [currentUser, setCurrentUser] = React.useState(null);
  const [viewAsUserListDto, setViewAsUserListDto] = React.useState<UserListResponseDto>(null);
  const [activeLink, setActiveLink] = React.useState<string>(PageName.overall);
  const [,setRefresh] = React.useState(0);

  const history = useHistory();
  PagesHelper.setHistory(history);
  const location = useLocation();

  function viewAsUserOnChange(selectedValues: any) {
    const pageState : PageState = StateHelper.getPageState();

    if( selectedValues.length > 0 ){
      let userId : string = selectedValues[0].userId;
      pageState.viewAs = userId;
    }
    else {
      pageState.viewAs = null;
    }

    PagesHelper.historyPushFromPageState();
    usersMe(setCurrentUser, findUsersList, setError);
  }

  function findUsersList(includeViewAsUser : boolean){
    const config = getHeadersConfig(includeViewAsUser);
    const url = joinURLs("2/users/list", process.env.REACT_APP_REPORTING_API_BASE_URL);
    axios
      .get(url, config)
      .then(function (response: any) {

        const dto : UserListResponseDto =  {
          status : ResponseStatus.OK, 
          error : null,
          userListResponse : response.data.users
        };

        setViewAsUserListDto(dto);
        StateHelper.setUserListResponseDto(dto);
      })
      .catch(function (error) {
        const isLoggedOut = ErrorHandler.handleAuthError(error, setError);
        if (!isLoggedOut) {
          const dto : UserListResponseDto =  {
            status : ResponseStatus.ERROR, 
            error : null,
            userListResponse : null
          };
          setViewAsUserListDto(dto);
          StateHelper.setUserListResponseDto(dto);
        }
      });
  }

  const envBgColor = process.env.REACT_APP_ENV === "production" ? "bg-dark" : process.env.REACT_APP_ENV === "development" ? "bg-secondary" : "bg-info";

  const productsUrl = "/products" + PagesHelper.getInitialPageUrl(PageName.overall);

  let pageContainer : any = null;
  if (isLoading || (!isAuthenticated && !error)) {
    pageContainer = <Loading />;
  } else if (!isAuthenticated || error) {
    pageContainer =  <ErrorPage />;
  } else {
    pageContainer = <PageContainer
                      currentUser={currentUser}
                      productsUrl={productsUrl}
                    />;
  }

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    ErrorHandler.setGlobalLoginRedirectHandler(loginWithRedirect);
  }, [loginWithRedirect]);

  useEffect(() => {
    if (isAuthenticated) {
      usersMe(setCurrentUser, findUsersList, setError);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const previousPage = StateHelper.getCurrentPage();
    // check history action POP to catch history reload, back and forward. Then, update page state from url
    if (history.action === "POP") {
      urlParser.parseUrl(location.pathname + location.search, setActiveLink);
      // Get the new current page after parsing
      const newPage = StateHelper.getCurrentPage();
      // search in case the page don't change
      if (previousPage === newPage) {
        const searchFunction = StateHelper.getSearchFuntion(newPage);
        if (searchFunction) {
          searchFunction(false);
        }
      }
      // refresh to re render CurrentUserBar component and update view as form
      setRefresh(Date.now());
    }
  }, [location, history] );

  return (
    <>
      <CurrentUserBar currentUser={currentUser} viewAsUserListDto={viewAsUserListDto} onChange={viewAsUserOnChange}
        activeLink={activeLink} setActiveLink={setActiveLink} envBgColor={envBgColor}
      />

      <Container fluid>
        {pageContainer}
      </Container>
    </>
  );
}

Container.propTypes = {
  currentUser: PropTypes.object,
  productsUrl: PropTypes.string,
}

export default App;
